<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div>
          <figure>
            <img
              :alt="currentStep.answer"
              :src="currentStep?.activityImage?.image?.src"
              v-if="currentStep?.activityImage?.image?.src"
            />
          </figure>
          <div id="steps">
            <text-display-list
              element="div"
              id="input"
              text-size="small"
              v-for="(step, stepIndex) in currentRound.steps"
              v-show="stepIndex <= progress.stepIndex"
              :key="stepIndex"
            >
              <color-block-text :text="step.answer" :text-step="stepIndex"></color-block-text>
            </text-display-list>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';
import ColorBlockText from 'components/text-displays/ColorBlockText.vue';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  components: {
    ActivityStage,
    ColorBlockText,
    TextDisplayList
  },
  data() {
    return {
      activityName: 'Rhymes With',
      sfx: null
    };
  },

  methods: {
    getActivityState() {
      const {
        rounds,
        keyboard: { characters }
      } = this.activityData;
      const isTouchHold = !!characters && characters.includes('F') !== characters.includes('J');

      return {
        rounds,
        instructions: {
          written: isTouchHold ? 'Hold the target. Type each letter' : 'Type the rhyming words.',
          voice: `${this.voiceInstructionsPrefix}/rhymeswith${
            isTouchHold ? '' : 'nth'
          }-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    async loadImages() {
      this.rounds.forEach(round => {
        round.steps.forEach(step => this.queueImage(step.activityImage?.image?.src));
      });

      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(({ steps }) => {
        steps.forEach(({ answerAudio }) => {
          this.queueAudio(answerAudio?.audio?.src);
        });
      });

      const cache = await this.downloadAudio();

      return this.rounds.map(({ steps }) =>
        steps.map(({ answerAudio }) => cache.get(answerAudio?.audio?.src))
      );
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setRound();

      this.$nextTick(this.setActivityContainerHeight);
    },

    setRound() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      const { roundIndex, stepIndex } = this.progress;

      if (this.isEndOfCurrentSentence && !this.isEndOfRound) {
        this.sfx[roundIndex][stepIndex].play();
        this.sndBell();
        this.setCurrentStep();
      }

      if (this.isEndOfRound) {
        this.sfx[roundIndex][stepIndex].play();
        this.sndBell();
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const t = new TimelineLite();

      t.delay(0.8).call(() => {
        if (this.isLastRound) {
          TweenMax.delayedCall(1.5, this.completeActivity);
        } else {
          t.delay(1.5)
            .to('.activity-stage', 0.5, { opacity: 0 })
            .call(this.setRound);
        }
      });
      this.sndBell();
    }
  },
  watch: {
    currentStep(newVal) {
      if (newVal) {
        this.setCurrentKeybordingSegment('');
        this.setTotalKeyboardingSegment(newVal.answer);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
div:first-of-type {
  margin: auto;

  figure {
    height: 25vh;
    margin: auto;

    img {
      display: block;
      height: 100%;
      margin: auto;
      width: auto;
    }
  }

  #steps {
    margin: 20px auto 0px auto;
    max-width: 700px;
    display: flex;
    flex-direction: row;
    justify-content: center;

    #input {
      margin: auto 2.5%;
      font-size: 2vw;
    }
  }
}
</style>
